import React, { useRef, useEffect, useState, useCallback, MouseEvent } from 'react';
import { apiClient } from '../lib/api';
import { PhotoIcon } from '@heroicons/react/24/outline';

// --- ИНТЕРФЕЙСЫ И КОНСТАНТЫ ---

interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}

const BLOCK_TYPES = [
  { label: 'Заголовок 1', value: 'h1' },
  { label: 'Заголовок 2', value: 'h2' },
  { label: 'Заголовок 3', value: 'h3' },
  { label: 'Заголовок 4', value: 'h4' },
  { label: 'Заголовок 5', value: 'h5' },
  { label: 'Заголовок 6', value: 'h6' },
  { label: 'Параграф', value: 'p' },
];

const INLINE_STYLES = [
  { label: 'Bold', value: 'bold', icon: <span style={{fontWeight: 'bold'}}>B</span> },
  { label: 'Italic', value: 'italic', icon: <span style={{fontStyle: 'italic'}}>I</span> },
  { label: 'Underline', value: 'underline', icon: <span style={{textDecoration: 'underline'}}>U</span> },
  { label: 'Strikethrough', value: 'strikeThrough', icon: <span style={{textDecoration: 'line-through'}}>S</span> },
  { label: 'Superscript', value: 'superscript', icon: <span>X<sup>2</sup></span> },
  { label: 'Subscript', value: 'subscript', icon: <span>X<sub>2</sub></span> },
];

const ALIGNMENT_STYLES = [
    { command: 'justifyLeft', value: 'left', icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M15 15H3v2h12v-2zm0-8H3v2h12V7zM3 13h18v-2H3v2zm0 8h18v-2H3v2zM3 3v2h18V3H3z"></path></svg> },
    { command: 'justifyCenter', value: 'center', icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M7 15v2h10v-2H7zm-4 6h18v-2H3v2zm0-8h18v-2H3v2zm4-6v2h10V7H7zM3 3v2h18V3H3z"></path></svg> },
    { command: 'justifyRight', value: 'right', icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M3 21h18v-2H3v2zm6-4h12v-2H9v2zm-6-4h18v-2H3v2zm6-4h12V7H9v2zM3 3v2h18V3H3z"></path></svg> },
    { command: 'justifyFull', value: 'justify', icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M3 21h18v-2H3v2zm0-4h18v-2H3v2zm0-4h18v-2H3v2zm0-4h18V7H3v2zm0-6v2h18V3H3z"></path></svg> },
];

const LINK_ICON = <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M3.9 12c0-1.71 1.39-3.1 3.1-3.1h4V7H7c-2.76 0-5 2.24-5 5s2.24 5 5 5h4v-1.9H7c-1.71 0-3.1-1.39-3.1-3.1zM8 13h8v-2H8v2zm9-6h-4v1.9h4c1.71 0 3.1 1.39 3.1 3.1s-1.39 3.1-3.1 3.1h-4V17h4c2.76 0 5-2.24 5-5s-2.24-5-5-5z"></path></svg>;

const DEFAULT_BLOCK = 'p';
const DEFAULT_ALIGN = 'left';

// --- КОМПОНЕНТ ---

const RichTextEditor: React.FC<RichTextEditorProps> = ({ value, onChange, placeholder }) => {
  const editorRef = useRef<HTMLDivElement>(null);
  const [mode, setMode] = useState<'visual' | 'code'>('visual');
  const [blockType, setBlockType] = useState(DEFAULT_BLOCK);
  const [textAlign, setTextAlign] = useState<string>(DEFAULT_ALIGN);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [activeStyles, setActiveStyles] = useState<Record<string, boolean>>({});
  const [activeListType, setActiveListType] = useState<'ul' | 'ol' | null>(null);

  const [isLinkModalOpen, setIsLinkModalOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [linkTitle, setLinkTitle] = useState('');
  const [isSpecialLink, setIsSpecialLink] = useState(false);
  const savedSelection = useRef<Range | null>(null);

  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [imageSrc, setImageSrc] = useState('');
  const [imageAlt, setImageAlt] = useState('');
  const [pendingImageInsert, setPendingImageInsert] = useState<{src: string, alt: string} | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isImageUploading, setIsImageUploading] = useState(false);
  const [imageUploadError, setImageUploadError] = useState<string | null>(null);

  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
  const [videoSrc, setVideoSrc] = useState('');
  const [videoUploadError, setVideoUploadError] = useState<string | null>(null);
  const [isVideoUploading, setIsVideoUploading] = useState(false);
  const videoFileInputRef = useRef<HTMLInputElement>(null);

  const isUpdatingFromValue = useRef(false);
  const isUserTyping = useRef(false);
  const typingTimeoutRef = useRef<number | null>(null);

  // --- ФУНКЦИИ ---

  const ensureInitialContent = useCallback(() => {
    if (editorRef.current) {
        const isEmpty = !editorRef.current.innerHTML || editorRef.current.innerHTML === '<br>';
        if (isEmpty) {
            editorRef.current.innerHTML = `<p><br></p>`;
            const range = document.createRange();
            const sel = window.getSelection();
            if (sel && editorRef.current.firstChild) {
              range.setStart(editorRef.current.firstChild, 0);
              range.collapse(true);
              sel.removeAllRanges();
              sel.addRange(range);
            }
        }
    }
  }, []);

  // Функция для преобразования абсолютных URL обратно в относительные пути
  const convertAbsoluteToRelative = (html: string): string => {
    if (!html) return html;
    const baseUrl = 'https://api.parkdubki.ru';
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    
    // Находим все изображения
    const images = tempDiv.querySelectorAll('img');
    images.forEach((img) => {
      const src = img.getAttribute('src');
      if (src && src.startsWith(baseUrl)) {
        // Удаляем базовый URL, оставляя только относительный путь
        const relativePath = src.replace(baseUrl, '');
        img.setAttribute('src', relativePath);
      }
    });
    
    // Находим все видео
    const videos = tempDiv.querySelectorAll('video');
    videos.forEach((video) => {
      const src = video.getAttribute('src');
      if (src && src.startsWith(baseUrl)) {
        const relativePath = src.replace(baseUrl, '');
        video.setAttribute('src', relativePath);
      }
    });
    
    return tempDiv.innerHTML;
  };

  // Функция для преобразования относительных путей в абсолютные URL
  const convertRelativeToAbsolute = (html: string): string => {
    if (!html) return html;
    const baseUrl = 'https://api.parkdubki.ru';
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    
    // Находим все изображения
    const images = tempDiv.querySelectorAll('img');
    images.forEach((img) => {
      const src = img.getAttribute('src');
      if (src && !src.startsWith('http://') && !src.startsWith('https://')) {
        // Преобразуем относительный путь в абсолютный
        const path = src.startsWith('/') ? src : `/${src}`;
        img.setAttribute('src', `${baseUrl}${path}`);
      } else if (src && src.startsWith(baseUrl)) {
        // Если уже абсолютный URL с нашим базовым URL, оставляем как есть
      }
    });
    
    // Находим все видео
    const videos = tempDiv.querySelectorAll('video');
    videos.forEach((video) => {
      const src = video.getAttribute('src');
      if (src && !src.startsWith('http://') && !src.startsWith('https://')) {
        const path = src.startsWith('/') ? src : `/${src}`;
        video.setAttribute('src', `${baseUrl}${path}`);
      }
    });
    
    return tempDiv.innerHTML;
  };

  // Сохраняем позицию курсора (сохраняем сам Range)
  const saveCursorPosition = useCallback(() => {
    const selection = window.getSelection();
    if (!selection || !selection.rangeCount || !editorRef.current) return null;
    
    const range = selection.getRangeAt(0);
    // Проверяем, что курсор находится внутри редактора
    if (!editorRef.current.contains(range.commonAncestorContainer)) {
      return null;
    }
    
    // Сохраняем Range как есть
    return range.cloneRange();
  }, []);

  // Восстанавливаем позицию курсора
  const restoreCursorPosition = useCallback((savedRange: Range | null) => {
    if (!savedRange || !editorRef.current) return;
    
    const selection = window.getSelection();
    if (!selection) return;
    
    try {
      // Проверяем, что сохраненный Range все еще валиден
      if (editorRef.current.contains(savedRange.commonAncestorContainer)) {
        selection.removeAllRanges();
        selection.addRange(savedRange);
        return;
      }
    } catch (e) {
      // Если Range невалиден, пытаемся восстановить по-другому
    }
    
    // Fallback: ставим курсор в конец
    const range = document.createRange();
    range.selectNodeContents(editorRef.current);
    range.collapse(false);
    selection.removeAllRanges();
    selection.addRange(range);
  }, []);

  const handleInput = useCallback(() => {
    if (editorRef.current && !isUpdatingFromValue.current) {
      // Помечаем, что пользователь печатает
      isUserTyping.current = true;
      
      // Очищаем предыдущий таймаут
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      
      // Через 100ms после окончания ввода снимаем флаг
      typingTimeoutRef.current = window.setTimeout(() => {
        isUserTyping.current = false;
      }, 100);
      
      let currentHTML = editorRef.current.innerHTML;
      if (currentHTML === '' || currentHTML === '<br>') {
        ensureInitialContent();
        currentHTML = editorRef.current.innerHTML;
      }
      // Преобразуем абсолютные пути обратно в относительные перед сохранением
      const relativeHTML = convertAbsoluteToRelative(currentHTML);
      onChange(relativeHTML);
    }
  }, [onChange, ensureInitialContent]);

  const detectActiveStyles = useCallback(() => {
    const newActiveStyles: Record<string, boolean> = {};
    const selection = window.getSelection();

    INLINE_STYLES.forEach(style => {
        try {
            newActiveStyles[style.value] = document.queryCommandState(style.value);
        } catch (e) {
            console.error(e);
            newActiveStyles[style.value] = false;
        }
    });

    // --- ИСПРАВЛЕНИЕ: Предотвращаем "прилипание" стиля от ссылки ---
    if (selection && selection.isCollapsed) {
        let parentNode = selection.anchorNode;
        if (parentNode?.nodeType !== Node.ELEMENT_NODE) {
            parentNode = parentNode?.parentNode || null;
        }
        // Если курсор находится внутри или рядом со ссылкой, но текст не выделен,
        // принудительно считаем 'underline' неактивным.
        if (parentNode && (parentNode as HTMLElement).closest('a')) {
            newActiveStyles['underline'] = false;
        }
    }

    setActiveStyles(newActiveStyles);
  }, []);

  const detectCurrentBlockType = useCallback(() => {
    const selection = window.getSelection();
    if (!selection?.rangeCount) {
      setActiveListType(null);
      setBlockType(DEFAULT_BLOCK);
      return null;
    }
    
    let node = selection.getRangeAt(0).startContainer;
    
    // Проверяем, находимся ли мы внутри списка
    let listElement: HTMLElement | null = null;
    let currentNode: Node | null = node;
    
    while (currentNode && currentNode !== editorRef.current) {
      if (currentNode.nodeType === Node.ELEMENT_NODE) {
        const element = currentNode as HTMLElement;
        const tagName = element.tagName.toLowerCase();
        if (tagName === 'ul' || tagName === 'ol') {
          listElement = element;
          break;
        }
        if (tagName === 'li') {
          const parent = element.parentElement;
          if (parent && (parent.tagName.toLowerCase() === 'ul' || parent.tagName.toLowerCase() === 'ol')) {
            listElement = parent;
            break;
          }
        }
      }
      currentNode = currentNode.parentNode;
    }
    
    // Если находимся в списке, устанавливаем активный тип списка
    if (listElement) {
      const listTagName = listElement.tagName.toLowerCase();
      setActiveListType(listTagName === 'ul' ? 'ul' : 'ol');
    } else {
      setActiveListType(null);
    }
    
    // Определяем тип блока
    while (node && node.parentNode !== editorRef.current) {
        node = node.parentNode!;
    }

    if (node && node.nodeType === Node.ELEMENT_NODE) {
        const element = node as HTMLElement;
        const tagName = element.tagName.toLowerCase();
        
        // Если это элемент списка, не меняем blockType
        if (tagName === 'li') {
          return element;
        }
        
        const foundType = BLOCK_TYPES.find(type => type.value === tagName);
        if (foundType) {
            setBlockType(foundType.value);
            return element;
        }
    }
    
    setBlockType(DEFAULT_BLOCK);
    return null;
  }, []);

  const detectAlignment = useCallback((element: HTMLElement | null) => {
    if (element) {
        let align = window.getComputedStyle(element).textAlign;
        if (align === 'start') align = 'left';
        if (align === 'end') align = 'right';
        setTextAlign(align);
    } else {
        setTextAlign(DEFAULT_ALIGN);
    }
  }, []);

  const handleToolbarMouseDown = (e: MouseEvent) => {
    e.preventDefault();
  };

  const applyInlineStyle = (command: string) => {
    document.execCommand(command, false);
    detectActiveStyles();
    editorRef.current?.focus();
  };
  
  const applyAlignment = (command: string) => {
    document.execCommand(command, false);
    const block = detectCurrentBlockType();
    detectAlignment(block);
    editorRef.current?.focus();
  }

  const insertList = (listType: 'insertUnorderedList' | 'insertOrderedList') => {
    const editor = editorRef.current;
    if (!editor) return;
    
    const selection = window.getSelection();
    if (!selection?.rangeCount) return;
    
    const range = selection.getRangeAt(0);
    
    // Проверяем, находимся ли мы уже в списке
    let currentNode: Node | null = range.startContainer;
    let isInList = false;
    let currentListType: 'ul' | 'ol' | null = null;
    
    while (currentNode && currentNode !== editor) {
      if (currentNode.nodeType === Node.ELEMENT_NODE) {
        const element = currentNode as HTMLElement;
        const tagName = element.tagName.toLowerCase();
        if (tagName === 'ul') {
          isInList = true;
          currentListType = 'ul';
          break;
        } else if (tagName === 'ol') {
          isInList = true;
          currentListType = 'ol';
          break;
        }
      }
      currentNode = currentNode.parentNode;
    }
    
    // Если уже в списке того же типа, преобразуем в параграф
    if (isInList && 
        ((listType === 'insertUnorderedList' && currentListType === 'ul') ||
         (listType === 'insertOrderedList' && currentListType === 'ol'))) {
      // Преобразуем список в параграф
      document.execCommand('formatBlock', false, 'p');
      setActiveListType(null);
    } else {
      // Создаем или переключаем список
      document.execCommand(listType, false);
      setActiveListType(listType === 'insertUnorderedList' ? 'ul' : 'ol');
    }
    
    editor.focus();
    detectCurrentBlockType();
    handleInput();
  }
  
  const removeList = () => {
    document.execCommand('formatBlock', false, 'p');
    setActiveListType(null);
    editorRef.current?.focus();
    detectCurrentBlockType();
    handleInput();
  }
  
  const applyBlockType = useCallback((newBlockType: string) => {
    const editor = editorRef.current;
    const selection = window.getSelection();
    if (!editor || !selection?.rangeCount) return;

    const range = selection.getRangeAt(0);

    if (!range.collapsed) {
        document.execCommand('formatBlock', false, newBlockType);
    } 
    else {
        let currentBlock: Node | null = range.startContainer;
        while (currentBlock && currentBlock.parentNode !== editor) {
            currentBlock = currentBlock.parentNode;
        }

        const isBlockEffectivelyEmpty = !currentBlock || !currentBlock.textContent?.trim();

        if (isBlockEffectivelyEmpty) {
             document.execCommand('formatBlock', false, newBlockType);
        } 
        else {
            const newElement = document.createElement(newBlockType);
            newElement.innerHTML = '<br>';

            if (currentBlock && currentBlock.parentNode === editor) {
                (currentBlock as HTMLElement).after(newElement);
            } else {
                editor.appendChild(newElement);
            }

            const newRange = document.createRange();
            newRange.setStart(newElement, 0);
            newRange.collapse(true);
            selection.removeAllRanges();
            selection.addRange(newRange);
        }
    }

    setIsDropdownOpen(false);
    setBlockType(newBlockType);
    editor.focus();
    handleInput();
  }, [handleInput]);

  const handleKeyDown = useCallback((event: React.KeyboardEvent<HTMLDivElement>) => {
    const selection = window.getSelection();
    if (!selection?.rangeCount) return;
    
    const range = selection.getRangeAt(0);
    
    // Проверяем, находимся ли мы в списке
    let currentNode: Node | null = range.startContainer;
    let isInList = false;
    let listElement: HTMLElement | null = null;
    let listItem: HTMLElement | null = null;
    
    while (currentNode && currentNode !== editorRef.current) {
      if (currentNode.nodeType === Node.ELEMENT_NODE) {
        const element = currentNode as HTMLElement;
        const tagName = element.tagName.toLowerCase();
        if (tagName === 'ul' || tagName === 'ol') {
          isInList = true;
          listElement = element;
          break;
        } else if (tagName === 'li') {
          listItem = element;
          const parent = element.parentElement;
          if (parent && (parent.tagName.toLowerCase() === 'ul' || parent.tagName.toLowerCase() === 'ol')) {
            isInList = true;
            listElement = parent;
          }
        }
      }
      currentNode = currentNode.parentNode;
    }
    
    if (event.key === 'Enter' && !event.shiftKey) {
        if (isInList && listItem) {
          const listItemText = listItem.textContent?.trim() || '';
          const isListItemEmpty = listItemText === '' || listItemText.length === 0;
          
          // Если элемент списка пуст и курсор в начале, преобразуем в параграф
          if (isListItemEmpty && range.collapsed && range.startOffset === 0) {
            event.preventDefault();
            removeList();
            return;
          }
          
          // В списке - используем стандартное поведение браузера для создания нового li
          // После создания нового элемента обновляем состояние
          setTimeout(() => {
            detectCurrentBlockType();
            handleInput();
          }, 0);
          return; // Разрешаем стандартное поведение
        } else {
          // Не в списке - создаем новый параграф
          event.preventDefault();

          // Находим текущий блок
          let currentBlock: Node | null = range.startContainer;
          while (currentBlock && currentBlock.parentNode !== editorRef.current) {
              currentBlock = currentBlock.parentNode;
          }

          // Создаем новый параграф
          const newParagraph = document.createElement('p');
          newParagraph.innerHTML = '<br>';

          // Вставляем новый параграф после текущего блока
          if (currentBlock && currentBlock.parentNode === editorRef.current) {
              (currentBlock as HTMLElement).after(newParagraph);
          } else {
              // Если мы не в блоке, просто добавляем в конец
              editorRef.current?.appendChild(newParagraph);
          }

          // Перемещаем курсор в новый параграф
          const newRange = document.createRange();
          newRange.setStart(newParagraph, 0);
          newRange.collapse(true);
          selection.removeAllRanges();
          selection.addRange(newRange);
          
          handleInput();
        }
    } else if (event.key === 'Backspace' && isInList && listItem) {
      // Обработка Backspace в списке
      const listItemText = listItem.textContent?.trim() || '';
      const isListItemEmpty = listItemText === '' || listItemText.length === 0;
      
      if (isListItemEmpty && range.collapsed && range.startOffset === 0) {
        // Если элемент списка пуст и курсор в начале, удаляем элемент
        event.preventDefault();
        
        const list = listItem.parentElement;
        const nextSibling = listItem.nextElementSibling;
        const prevSibling = listItem.previousElementSibling;
        
        // Если это последний элемент в списке, преобразуем список в параграф
        if (!nextSibling && !prevSibling) {
          const newParagraph = document.createElement('p');
          newParagraph.innerHTML = '<br>';
          list?.replaceWith(newParagraph);
          
          const newRange = document.createRange();
          newRange.setStart(newParagraph, 0);
          newRange.collapse(true);
          selection.removeAllRanges();
          selection.addRange(newRange);
          
          setActiveListType(null);
        } else {
          // Удаляем элемент списка
          listItem.remove();
          
          // Перемещаем курсор в предыдущий или следующий элемент
          if (prevSibling) {
            const newRange = document.createRange();
            newRange.selectNodeContents(prevSibling);
            newRange.collapse(false);
            selection.removeAllRanges();
            selection.addRange(newRange);
          } else if (nextSibling) {
            const newRange = document.createRange();
            newRange.setStart(nextSibling, 0);
            newRange.collapse(true);
            selection.removeAllRanges();
            selection.addRange(newRange);
          }
        }
        
        handleInput();
      }
    }
  }, [handleInput]);

  // --- ✅ START: ИСПРАВЛЕННАЯ ЛОГИКА ВСТАВКИ МЕДИА ---

  /**
   * Вставляет медиа-элемент (img, video) в редактор, заменяя пустые блоки 
   * или вставляя после блоков с контентом.
   * @param mediaElement HTML-элемент для вставки (например, <img> или <video>)
   */
  const insertMediaElement = (mediaElement: HTMLElement) => {
    const editor = editorRef.current;
    if (!editor) return;

        if (editor.children.length === 1 && editor.firstElementChild) {
        const onlyBlock = editor.firstElementChild as HTMLElement;
        const isBlockEffectivelyEmpty = !onlyBlock.textContent?.trim() && !onlyBlock.querySelector('img, video, hr');
        
        if (isBlockEffectivelyEmpty) {
            // Полностью заменяем содержимое редактора на медиа-элемент
            editor.innerHTML = '';
            editor.appendChild(mediaElement);

            // Устанавливаем курсор после элемента
            const selection = window.getSelection();
            if (selection) {
                const newRange = document.createRange();
                newRange.setStartAfter(mediaElement);
                newRange.collapse(true);
                selection.removeAllRanges();
                selection.addRange(newRange);
            }
            
            handleInput();
            return; // Завершаем выполнение, так как задача решена
        }
    }

    editor.focus();
    restoreSelection();

    const selection = window.getSelection();
    if (!selection || !selection.rangeCount) return;

    const range = selection.getRangeAt(0);

    // Находим корневой блок, в котором находится курсор
    let rootBlock: Node | null = range.startContainer;
    if (rootBlock.nodeType === Node.TEXT_NODE) {
        rootBlock = rootBlock.parentNode;
    }
    while (rootBlock && rootBlock.parentNode !== editor && rootBlock !== editor) {
        rootBlock = rootBlock.parentNode;
    }
    
    // Если мы вышли за пределы редактора, считаем, что блока нет
    if (rootBlock === editor) {
        rootBlock = null;
    }

    if (rootBlock && rootBlock instanceof HTMLElement) {
        const isBlockEmpty = !rootBlock.textContent?.trim() && !rootBlock.querySelector('img, video, hr');
        
        if (isBlockEmpty) {
            // Если блок пуст (например, <p><br></p>), заменяем его на медиа-элемент
            rootBlock.replaceWith(mediaElement);
        } else {
            // Если в блоке есть контент, вставляем медиа-элемент после него
            rootBlock.after(mediaElement);
        }
    } else {
        // Fallback: если не удалось найти блок, вставляем по курсору
        range.deleteContents();
        range.insertNode(mediaElement);
    }
    
    // Устанавливаем курсор после вставленного медиа-элемента
    const newRange = document.createRange();
    newRange.setStartAfter(mediaElement);
    newRange.collapse(true);
    selection.removeAllRanges();
    selection.addRange(newRange);

    handleInput();
  };

  const insertImage = (src: string, alt: string) => {
    const img = document.createElement('img');
    // Преобразуем относительные пути в абсолютные для отображения
    const fullUrl = apiClient.getMediaUrl(src);
    img.src = fullUrl;
    img.alt = alt;
    img.setAttribute('loading', 'lazy');
    insertMediaElement(img);
  };
  
  const insertVideo = (src: string) => {
    const video = document.createElement('video');
    // Преобразуем относительные пути в абсолютные для отображения
    const fullUrl = apiClient.getMediaUrl(src);
    video.src = fullUrl;
    insertMediaElement(video);
  };

  // --- ✅ END: ИСПРАВЛЕННАЯ ЛОГИКА ВСТАВКИ МЕДИА ---


  // --- Загрузка и обработка модальных окон (без изменений) ---

  const openImageModal = (src: string, isUploaded = false) => {
    setImageSrc(src);
    setImageAlt('');
    setIsImageModalOpen(true);
    setPendingImageInsert({ src, alt: '' });
    setImageUploadError(null);
    setIsImageUploading(false);
  };

  const uploadImageToServer = async (file: File) => {
    setIsImageUploading(true);
    setImageUploadError(null);
    try {
      const formData = new FormData();
      formData.append('image', file);
      const response = await apiClient.uploadImage(formData);
      const url = response.data.url;
      openImageModal(url, true);
    } catch (e: any) {
      setImageUploadError(e?.response?.data?.error || 'Ошибка загрузки изображения');
    } finally {
      setIsImageUploading(false);
    }
  };
  
  // ✅ FIX: Добавлен недостающий обработчик
  const handleImageModalInsert = () => {
    if (pendingImageInsert && imageAlt) {
      insertImage(pendingImageInsert.src, imageAlt);
      closeImageModal();
    }
  };

  const closeImageModal = () => {
    setIsImageModalOpen(false);
    setImageSrc('');
    setImageAlt('');
    setPendingImageInsert(null);
    setImageUploadError(null);
    setIsImageUploading(false);
    editorRef.current?.focus();
  };

  const openVideoModal = (src: string) => {
    setVideoSrc(src);
    setIsVideoModalOpen(true);
    setVideoUploadError(null);
    setIsVideoUploading(false);
  };
  const closeVideoModal = () => {
    setIsVideoModalOpen(false);
    setVideoSrc('');
    setVideoUploadError(null);
    setIsVideoUploading(false);
    editorRef.current?.focus();
  };
  const handleVideoModalInsert = () => {
    if (videoSrc) {
      insertVideo(videoSrc);
      closeVideoModal();
    }
  };

  const uploadVideoToServer = async (file: File) => {
    setIsVideoUploading(true);
    setVideoUploadError(null);
    try {
      const formData = new FormData();
      formData.append('video', file);
      const response = await apiClient.uploadVideo(formData);
      const url = response.data.url;
      openVideoModal(url);
    } catch (e: any) {
      setVideoUploadError(e?.response?.data?.error || 'Ошибка загрузки видео');
    } finally {
      setIsVideoUploading(false);
    }
  };

  const handleDrop = useCallback((event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    const files = event.dataTransfer.files;
    if (files && files.length > 0) {
      const file = files[0];
      if (file.type.startsWith('image/')) {
        uploadImageToServer(file);
        saveSelection();
      } else if (file.type.startsWith('video/')) {
        uploadVideoToServer(file);
        saveSelection();
      }
    }
  }, []);

  const handlePaste = useCallback((event: React.ClipboardEvent<HTMLDivElement>) => {
    const clipboardData = event.clipboardData;
    const items = clipboardData.items;
    let handled = false;
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.type.startsWith('image/')) {
        event.preventDefault();
        const file = item.getAsFile();
        if (file) {
          uploadImageToServer(file);
          saveSelection();
        }
        handled = true;
        break;
      } else if (item.type.startsWith('video/')) {
        event.preventDefault();
        const file = item.getAsFile();
        if (file) {
          uploadVideoToServer(file);
          saveSelection();
        }
        handled = true;
        break;
      }
    }
    if (!handled) {
      event.preventDefault();
      let pastedHtml = clipboardData.getData('text/html');
      const pastedText = clipboardData.getData('text/plain');
      if (pastedHtml) {
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = pastedHtml;
        const allElements = tempDiv.getElementsByTagName('*');
        for (let i = 0; i < allElements.length; i++) {
          if (allElements[i].tagName.toLowerCase() === 'a') {
              const href = allElements[i].getAttribute('href');
              const target = allElements[i].getAttribute('target');
              const special = allElements[i].getAttribute('data-special-link');
              while(allElements[i].attributes.length > 0) {
                  allElements[i].removeAttribute(allElements[i].attributes[0].name);
              }
              if (href) allElements[i].setAttribute('href', href);
              if (target) allElements[i].setAttribute('target', target);
              if (special) allElements[i].setAttribute('data-special-link', special);
          } else {
              const attributes = Array.from(allElements[i].attributes);
              for (const attr of attributes) {
                  allElements[i].removeAttribute(attr.name);
              }
          }
        }
        const divs = tempDiv.getElementsByTagName('div');
        while (divs.length > 0) {
          const p = document.createElement('p');
          p.innerHTML = divs[0].innerHTML;
          divs[0].parentNode?.replaceChild(p, divs[0]);
        }
        const allowedTags = ['p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'span', 'br', 'b', 'i', 'u', 'strike', 'sub', 'sup', 'a', 'ul', 'ol', 'li'];
        const allFilteredElements = tempDiv.getElementsByTagName('*');
        for (let i = allFilteredElements.length - 1; i >= 0; i--) {
          if (!allowedTags.includes(allFilteredElements[i].tagName.toLowerCase())) {
            const parent = allFilteredElements[i].parentNode;
            while (allFilteredElements[i].firstChild) {
              parent?.insertBefore(allFilteredElements[i].firstChild as Node, allFilteredElements[i]);
            }
            parent?.removeChild(allFilteredElements[i]);
          }
        }
        pastedHtml = tempDiv.innerHTML;
        document.execCommand('insertHTML', false, pastedHtml);
      } else {
        document.execCommand('insertText', false, pastedText);
      }
      handleInput();
    }
  }, [handleInput]);

  const handleImageButtonClick = () => {
    fileInputRef.current?.click();
  };
  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && file.type.startsWith('image/')) {
      uploadImageToServer(file);
      saveSelection();
    }
    e.target.value = '';
  };
  const handleVideoButtonClick = () => {
    videoFileInputRef.current?.click();
  };
  const handleVideoFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && file.type.startsWith('video/')) {
      uploadVideoToServer(file);
      saveSelection();
    }
    e.target.value = '';
  };

  const saveSelection = () => {
    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0) {
        savedSelection.current = selection.getRangeAt(0).cloneRange();
    }
  };

  const restoreSelection = () => {
    if (savedSelection.current) {
        const selection = window.getSelection();
        selection?.removeAllRanges();
        selection?.addRange(savedSelection.current);
    }
  };
  
  const openLinkModal = () => {
    saveSelection();
    const selection = window.getSelection();
    setLinkTitle(selection?.toString() || '');
    setIsLinkModalOpen(true);
  };
  
  const closeLinkModal = () => {
    setIsLinkModalOpen(false);
    setLinkUrl('');
    setLinkTitle('');
    setIsSpecialLink(false);
    editorRef.current?.focus();
  };
  
  const handleInsertLink = () => {
    if (!linkUrl) return;

    const editor = editorRef.current;
    if (!editor) return;

    editor.focus();
    restoreSelection();

    const selection = window.getSelection();
    if (!selection || !selection.rangeCount) return;

    const range = selection.getRangeAt(0);

    const linkNode = document.createElement('a');
    linkNode.href = linkUrl;
    linkNode.target = '_blank';
    if (isSpecialLink) {
        linkNode.setAttribute('data-special-link', 'true');
    }
    linkNode.textContent = linkTitle || linkUrl;

    if (!range.collapsed) {
        range.deleteContents();
        range.insertNode(linkNode);
        if (linkNode.parentNode) {
            (linkNode.parentNode as HTMLElement).normalize();
        }
    } 
    else {
        let currentBlock: Node | null = range.startContainer;
        while (currentBlock && currentBlock.parentNode !== editor) {
            currentBlock = currentBlock.parentNode;
        }

        if (currentBlock && currentBlock.nodeType === Node.ELEMENT_NODE && currentBlock.parentNode === editor) {
            const blockElement = currentBlock as HTMLElement;
            if (!blockElement.textContent?.trim()) {
                editor.replaceChild(linkNode, blockElement);
            } else {
                blockElement.after(linkNode);
            }
        } 
        else {
            editor.innerHTML = ''; 
            editor.appendChild(linkNode);
        }
    }
    
    const newRange = document.createRange();
    newRange.setStartAfter(linkNode);
    newRange.collapse(true);
    selection.removeAllRanges();
    selection.addRange(newRange);
    
    handleInput();
    closeLinkModal();
  };


  // --- ЭФФЕКТЫ ---

  const handleSelectionChange = useCallback(() => {
    if (mode === 'visual' && document.activeElement === editorRef.current) {
      const block = detectCurrentBlockType();
      detectActiveStyles();
      detectAlignment(block);
    }
  }, [mode, detectCurrentBlockType, detectActiveStyles, detectAlignment]);

  useEffect(() => {
    const editor = editorRef.current;
    if (mode !== 'visual' || !editor) return;

    // Не обновляем содержимое, если пользователь активно печатает
    if (isUserTyping.current) {
      return;
    }

    // Преобразуем текущий HTML редактора для сравнения (убираем абсолютные пути)
    const currentRelativeHTML = convertAbsoluteToRelative(editor.innerHTML);
    // Нормализуем оба HTML для сравнения (убираем лишние пробелы и переносы строк)
    const normalizeHTML = (html: string) => {
      const temp = document.createElement('div');
      temp.innerHTML = html;
      return temp.innerHTML;
    };
    
    const normalizedValue = normalizeHTML(value || '<p><br></p>');
    const normalizedCurrent = normalizeHTML(currentRelativeHTML || '<p><br></p>');
    const shouldUpdate = normalizedValue !== normalizedCurrent || !editor.hasChildNodes();

    if (shouldUpdate) {
        // Сохраняем позицию курсора перед обновлением
        const cursorPosition = saveCursorPosition();
        
        isUpdatingFromValue.current = true;
        
        const contentToSet = (value && value !== '<br>') ? value : '<p><br></p>';
        // Преобразуем относительные пути в абсолютные для отображения
        const contentWithAbsolutePaths = convertRelativeToAbsolute(contentToSet);
        editor.innerHTML = contentWithAbsolutePaths;
        
        // Восстанавливаем позицию курсора после обновления
        setTimeout(() => {
          if (cursorPosition) {
            restoreCursorPosition(cursorPosition);
          } else {
            // Если не удалось сохранить позицию, ставим курсор в конец
            const selection = window.getSelection();
            if (selection) {
              const range = document.createRange();
              range.selectNodeContents(editor);
              range.collapse(false);
              selection.removeAllRanges();
              selection.addRange(range);
            }
          }
          isUpdatingFromValue.current = false;
        }, 0);
    }
  }, [value, mode, saveCursorPosition, restoreCursorPosition]);

  useEffect(() => {
    if (mode === 'visual' && editorRef.current) {
        const editor = editorRef.current;
        editor.focus({ preventScroll: true });

        const selection = window.getSelection();
        if (selection) {
            const range = document.createRange();
            range.selectNodeContents(editor);
            range.collapse(false); // в конец

            selection.removeAllRanges();
            selection.addRange(range);

            handleSelectionChange();
        }
    }
  }, [mode, handleSelectionChange]);

  useEffect(() => {
    ensureInitialContent();
  }, [ensureInitialContent]);

  useEffect(() => {
    document.addEventListener('selectionchange', handleSelectionChange);
    return () => {
      document.removeEventListener('selectionchange', handleSelectionChange);
      // Очищаем таймаут при размонтировании
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
    };
  }, [handleSelectionChange]);
  
  // --- РЕНДЕР ---

  return (
    // ✅ START: New structure for Vallorean Editor
    <div className="vallorean-editor border border-gray-300 rounded-lg shadow-sm">
      
      {/* Top bar for mode switcher */}
      <div className="flex justify-end p-2 bg-gray-200 border-b border-gray-300">
        <div className="flex gap-1">
          <button 
            type="button" 
            className={`px-3 py-1 text-sm rounded-md ${mode === 'visual' ? 'bg-blue-600 text-white shadow-sm' : 'bg-white text-gray-700 hover:bg-gray-50'}`} 
            onClick={() => setMode('visual')}
          >
            Редактор
          </button>
          <button 
            type="button" 
            className={`px-3 py-1 text-sm rounded-md ${mode === 'code' ? 'bg-blue-600 text-white shadow-sm' : 'bg-white text-gray-700 hover:bg-gray-50'}`} 
            onClick={() => setMode('code')}
          >
            Код
          </button>
        </div>
      </div>

      {/* Main Toolbar */}
      <div className="flex flex-wrap items-center justify-start gap-x-4 gap-y-2 p-2 bg-gray-50 border-b border-gray-300">
        {/* Block styles */}
        <div className="relative">
          <button
            type="button"
            className="px-3 py-2 border border-gray-300 rounded bg-white text-gray-700 flex items-center justify-center min-w-[140px] text-sm"
            onClick={() => setIsDropdownOpen(v => !v)}
            onMouseDown={handleToolbarMouseDown}
          >
            {BLOCK_TYPES.find(b => b.value === blockType)?.label || 'Параграф'}
            <svg className="ml-2 w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" /></svg>
          </button>
          {isDropdownOpen && (
            <div className="absolute z-10 mt-1 bg-white border rounded shadow w-full max-h-60 overflow-y-auto">
              {BLOCK_TYPES.map(b => (
                <button
                  key={b.value}
                  className={`block w-full text-left px-4 py-2 text-sm hover:bg-gray-100 ${blockType === b.value ? 'bg-blue-100 text-blue-700' : ''}`}
                  onClick={() => applyBlockType(b.value)}
                  onMouseDown={handleToolbarMouseDown}
                >
                  {b.label}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Inline styles */}
        <div className="flex items-center gap-1 border-l border-gray-300 pl-3">
          {INLINE_STYLES.map(style => (
            <button
              key={style.value}
              type="button"
              title={style.label}
              className={`w-8 h-8 rounded hover:bg-gray-200 flex items-center justify-center font-serif text-lg border border-gray-300 ${activeStyles[style.value] ? 'bg-blue-100 text-blue-700' : 'bg-white'}`}
              onMouseDown={handleToolbarMouseDown}
              onClick={() => applyInlineStyle(style.value)}
            >
              {style.icon}
            </button>
          ))}
        </div>
        
        {/* Alignment */}
        <div className="flex items-center gap-1 border-l border-gray-300 pl-3">
            {ALIGNMENT_STYLES.map(style => (
                <button
                    key={style.value}
                    type="button"
                    title={style.value}
                    className={`w-8 h-8 rounded hover:bg-gray-200 flex items-center justify-center border border-gray-300 ${textAlign === style.value ? 'bg-blue-100 text-blue-700' : 'bg-white'}`}
                    onMouseDown={handleToolbarMouseDown}
                    onClick={() => applyAlignment(style.command)}
                >
                    {style.icon}
                </button>
            ))}
        </div>

        {/* Lists */}
        <div className="flex items-center gap-1 border-l border-gray-300 pl-3">
            <button
                type="button"
                title="Маркированный список"
                className={`w-8 h-8 rounded hover:bg-gray-200 flex items-center justify-center border border-gray-300 ${activeListType === 'ul' ? 'bg-blue-100 text-blue-700' : 'bg-white'}`}
                onMouseDown={handleToolbarMouseDown}
                onClick={() => insertList('insertUnorderedList')}
            >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M4 10.5c-.83 0-1.5.67-1.5 1.5s.67 1.5 1.5 1.5 1.5-.67 1.5-1.5-.67-1.5-1.5-1.5zm0-6c-.83 0-1.5.67-1.5 1.5S3.17 7.5 4 7.5 5.5 6.83 5.5 6 4.83 4.5 4 4.5zm0 12c-.83 0-1.5.67-1.5 1.5s.67 1.5 1.5 1.5 1.5-.67 1.5-1.5-.67-1.5-1.5-1.5zM7 19h14v-2H7v2zm0-6h14v-2H7v2zm0-8v2h14V5H7z"/></svg>
            </button>
            <button
                type="button"
                title="Нумерованный список"
                className={`w-8 h-8 rounded hover:bg-gray-200 flex items-center justify-center border border-gray-300 ml-1 ${activeListType === 'ol' ? 'bg-blue-100 text-blue-700' : 'bg-white'}`}
                onMouseDown={handleToolbarMouseDown}
                onClick={() => insertList('insertOrderedList')}
            >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M2 17h2v.5H3v1h1v.5H2v1h3v-4H2v1zm1-9h1V4H2v1h1v3zm-1 3h1.8L2 13.1v.9h3v-1H3.2L5 11.9V11H2v1zm5-6v2h14V5H7zm0 14h14v-2H7v2zm0-6h14v-2H7v2z"/></svg>
            </button>
        </div>

        {/* Link */}
        <div className="flex items-center gap-1 border-l border-gray-300 pl-3">
            <button
                type="button"
                title="Вставить ссылку"
                className="w-8 h-8 rounded hover:bg-gray-200 flex items-center justify-center bg-white border border-gray-300"
                onMouseDown={handleToolbarMouseDown}
                onClick={openLinkModal}
            >
                {LINK_ICON}
            </button>
            {/* Кнопка загрузки изображения */}
            <button
                type="button"
                title="Вставить изображение"
                className="w-8 h-8 rounded hover:bg-gray-200 flex items-center justify-center bg-white border border-gray-300 ml-1"
                onMouseDown={handleToolbarMouseDown}
                onClick={handleImageButtonClick}
            >
                <PhotoIcon className="w-5 h-5 text-gray-700" />
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={handleFileInputChange}
            />
            {/* Кнопка загрузки видео */}
            <button
                type="button"
                title="Вставить видео"
                className="w-8 h-8 rounded hover:bg-gray-200 flex items-center justify-center bg-white border border-gray-300 ml-1"
                onMouseDown={handleToolbarMouseDown}
                onClick={handleVideoButtonClick}
            >
                <svg width="20" height="20" fill="none" viewBox="0 0 24 24"><path fill="currentColor" d="M17 10.5V7a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-3.5l4 4v-11l-4 4Z"/></svg>
            </button>
            <input
              ref={videoFileInputRef}
              type="file"
              accept="video/*"
              style={{ display: 'none' }}
              onChange={handleVideoFileInputChange}
            />
        </div>
      </div>

      {/* Editor Content Area */}
      <div
        className="rich-text-editor-content bg-white rounded-b-lg"
        onDrop={handleDrop}
        onDragOver={e => { e.preventDefault(); }}
      >
        {mode === 'visual' ? (
          <div
            ref={editorRef}
            contentEditable
            className="min-h-[400px] p-4 focus:outline-none"
            data-placeholder={placeholder}
            onInput={handleInput}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            onClick={handleSelectionChange}
            onKeyUp={handleSelectionChange}
          />
        ) : (
          <textarea
            className="w-full min-h-[400px] p-4 focus:outline-none font-mono text-sm bg-gray-800 text-gray-100 rounded-b-lg"
            value={value}
            onChange={e => onChange(e.target.value)}
          />
        )}
      </div>
      
      {/* Modal for inserting a link */}
      {isLinkModalOpen && (
        <div 
            className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center"
            onMouseDown={closeLinkModal}
        >
          <div 
            className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md"
            onMouseDown={e => e.stopPropagation()}
          >
            <h3 className="text-lg font-medium mb-4">Вставить ссылку</h3>
            <div className="space-y-4">
              <div>
                <label htmlFor="link-url" className="block text-sm font-medium text-gray-700">URL</label>
                <input
                  type="text"
                  id="link-url"
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  value={linkUrl}
                  onChange={e => setLinkUrl(e.target.value)}
                  placeholder="https://example.com"
                />
              </div>
              <div>
                <label htmlFor="link-title" className="block text-sm font-medium text-gray-700">Заголовок (необязательно)</label>
                <input
                  type="text"
                  id="link-title"
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  value={linkTitle}
                  onChange={e => setLinkTitle(e.target.value)}
                  placeholder="Текст для отображения"
                />
              </div>
              <div className="flex items-center">
                <input
                    id="special-link"
                    name="special-link"
                    type="checkbox"
                    className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    checked={isSpecialLink}
                    onChange={e => setIsSpecialLink(e.target.checked)}
                />
                <label htmlFor="special-link" className="ml-2 block text-sm text-gray-900">
                    Спец. ссылка
                </label>
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300"
                onClick={closeLinkModal}
              >
                Отмена
              </button>
              <button
                type="button"
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-blue-300"
                onClick={handleInsertLink}
                disabled={!linkUrl}
              >
                Вставить
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Modal for inserting an image */}
      {isImageModalOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center"
          onMouseDown={closeImageModal}
        >
          <div 
            className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md"
            onMouseDown={e => e.stopPropagation()}
          >
            <h3 className="text-lg font-medium mb-4">Вставить изображение</h3>
            <div className="space-y-4">
              <div>
                <label htmlFor="image-src" className="block text-sm font-medium text-gray-700">Источник (src)</label>
                <input
                  type="text"
                  id="image-src"
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  value={imageSrc}
                  readOnly
                />
              </div>
              <div>
                <label htmlFor="image-alt" className="block text-sm font-medium text-gray-700">Alt-текст (описание)</label>
                <input
                  type="text"
                  id="image-alt"
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  value={imageAlt}
                  onChange={e => setImageAlt(e.target.value)}
                  placeholder="Описание изображения (обязательно)"
                />
              </div>
              {isImageUploading && (
                <div className="text-center text-blue-600 text-sm">Загрузка изображения...</div>
              )}
              {imageUploadError && (
                <div className="text-center text-red-600 text-sm">{imageUploadError}</div>
              )}
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300"
                onClick={closeImageModal}
                disabled={isImageUploading}
              >
                Отмена
              </button>
              <button
                type="button"
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-blue-300"
                onClick={handleImageModalInsert}
                disabled={!imageAlt || isImageUploading}
              >
                Вставить
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Modal for inserting a video */}
      {isVideoModalOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center"
          onMouseDown={closeVideoModal}
        >
          <div 
            className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md"
            onMouseDown={e => e.stopPropagation()}
          >
            <h3 className="text-lg font-medium mb-4">Вставить видео</h3>
            <div className="space-y-4">
              <div>
                <label htmlFor="video-src" className="block text-sm font-medium text-gray-700">Источник (src)</label>
                <input
                  type="text"
                  id="video-src"
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  value={videoSrc}
                  readOnly
                />
              </div>
              {isVideoUploading && (
                <div className="text-center text-blue-600 text-sm">Загрузка видео...</div>
              )}
              {videoUploadError && (
                <div className="text-center text-red-600 text-sm">{videoUploadError}</div>
              )}
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300"
                onClick={closeVideoModal}
                disabled={isVideoUploading}
              >
                Отмена
              </button>
              <button
                type="button"
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-blue-300"
                onClick={handleVideoModalInsert}
                disabled={!videoSrc || isVideoUploading}
              >
                Вставить
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Styles */}
      <style dangerouslySetInnerHTML={{
        __html: `
          .rich-text-editor-content [contenteditable]:empty:before { 
            content: attr(data-placeholder); 
            color: #9ca3af; 
            pointer-events: none; 
            display: block; 
          }

          .rich-text-editor-content h1 { font-size: 2em; margin: 0.67em 0; }
          .rich-text-editor-content h2 { font-size: 1.5em; margin: 0.83em 0; }
          .rich-text-editor-content h3 { font-size: 1.17em; margin: 1em 0; }
          .rich-text-editor-content h4 { font-size: 1em; margin: 1.33em 0; }
          .rich-text-editor-content h5 { font-size: 0.83em; margin: 1.67em 0; }
          .rich-text-editor-content h6 { font-size: 0.67em; margin: 2.33em 0; }
          .rich-text-editor-content p { margin: 0; padding: 0.5em 0; }
          .rich-text-editor-content > div > *:first-child { margin-top: 0; }

          .rich-text-editor-content a {
            color: #0000ee;
            text-decoration: underline;
            text-underline-offset: 4px;
          }

          .rich-text-editor-content ul,
          .rich-text-editor-content ol {
            margin: 0.5em 0;
            padding-left: 2em;
          }

          .rich-text-editor-content ul {
            list-style-type: disc;
          }

          .rich-text-editor-content ol {
            list-style-type: decimal;
          }

          .rich-text-editor-content li {
            margin: 0.25em 0;
          }
        `
      }} />
    </div>
  );
};

export default RichTextEditor;